import React from 'react'

import PropTypes from 'prop-types'
import update from 'immutability-helper'
import { debounce } from 'throttle-debounce'

import showErrorNotification from 'Utils/showErrorNotification'
import getUtmCookies from 'Utils/utmCookies'

import InputField from 'Pods/Shared/InputField'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import { required, isEmail, isUsername } from 'Pods/Shared/Rules/Rules'
import ReactPasswordStrength from 'Pods/Shared/ReactPasswordStrength'

const fieldValidations = [
  RuleRunner('username', 'Username', required, isUsername),
  RuleRunner('email', 'Email', required, isEmail),
  RuleRunner('password', 'Password', required),
]

class GuestRegister extends React.Component {
  constructor(props) {
    super(props)

    this.state = this.getInitialState(props)
  }

  checkAccountAvailability = debounce(200, this.checkAccountAvailability)

  componentDidMount() {
    this.setState({ validationErrors: run(this.state, fieldValidations) })
  }

  getInitialState(props) {
    return {
      email: props.email,
      username: '',
      password: '',
      gdpr: 'undecided',
      availabilityNotifications: {
        username: { message: null, type: 'notification' },
        email: { message: null, type: 'notification' },
      },
      validationErrors: {},
    }
  }

  errorFor(field) {
    return this.state.validationErrors[field] || ''
  }

  handleInputChange = (e) => {
    const newState = update(this.state, { [e.target.name]: { $set: e.target.value } })
    newState.validationErrors = run(newState, fieldValidations)
    newState.showErrors = true

    if (e.target.name === 'username' && !newState.validationErrors.username) {
      this.checkAccountAvailability(newState.username, 'username')
    } else if (e.target.name === 'email' && !newState.validationErrors.email) {
      this.checkAccountAvailability(newState.email, 'email')
    }

    this.setState(newState)

    if (e.target.name === 'email') {
      this.props.updateEmail(newState.email)
    }
  }

  handlePasswordChange = (state) => {
    const newState = update(this.state, { password: { $set: state.password } })
    newState.validationErrors = run(newState, fieldValidations)
    newState.showErrors = true

    if (!state.isValid) {
      newState.validationErrors.password = 'Is too weak'
    } else {
      delete newState.validationErrors.password
    }

    this.setState(newState)
  }

  checkAccountAvailability(input, type) {
    axios.get(`/api/internal/users/${type}/availability`, { params: { [type]: input } })
      .then((response) => this.onCheckAccountAvailabilitySuccess(response, type))
      .catch((error) => { })
  }

  onCheckAccountAvailabilitySuccess(response, type) {
    const newState = this.state
    const capitalizedType = type[0].toUpperCase() + type.substring(1)

    if (response.data.success) {
      newState.availabilityNotifications[type].message = `${capitalizedType} is available`
      newState.availabilityNotifications[type].type = 'notification'
    } else {
      newState.availabilityNotifications[type].message = `${capitalizedType} is taken by another account`
      newState.availabilityNotifications[type].type = 'error'
    }

    this.setState(newState)
  }

  handleSubmit = (e) => {
    e.preventDefault()

    this.setState({ showErrors: true })

    const validationErrorsPresence = Object.getOwnPropertyNames(this.state.validationErrors).length !== 0

    const availabilityErrorsPresence = Object.values(
      this.state.availabilityNotifications
    ).filter(n => n.type === 'error').length !== 0

    if (validationErrorsPresence || availabilityErrorsPresence) {
      return null
    }

    const {
      referrer,
      raw_utm_string,
    } = getUtmCookies()

    const data = {
      new_user: {
        username: this.state.username,
        email: this.state.email,
        password: this.state.password,
        gdpr_consent: this.state.gdpr,
        source: this.props.source,
      },
      from_checkout: true,
      utm_statistics: {
        referrer,
        raw_utm_string,
      },
    }

    axios.post('/users', data)
      .then(this.onSubmitSuccess)
      .catch(this.onSubmitFailure)
  }

  onSubmitSuccess(response) {
    if (typeof response.data === 'string') {
      // Security tool asks for verification.
      document.write(response.data)
    } else {
      window.location = '/cg/checkout/payment'
    }
  }

  onSubmitFailure(xhr) {
    showErrorNotification(xhr.response?.data?.errors)
  }

  render() {
    return (
      <div className="user-auth-container">
        <form onSubmit={this.handleSubmit}>
          <InputField
            label="Email"
            id="email-guest"
            name="email"
            errorText={this.errorFor('email')}
            availabilityNotification={this.state.availabilityNotifications.email}
            showError={this.state.showErrors}
            value={this.state.email}
            formClassName="form-group-grey"
            inputClassName="form-control-grey form-control--with-placeholder"
            placeholder="email@example.org"
            onChange={this.handleInputChange}
          />
          {this.props.checkbox}
          <div className="form-group form-group-grey">
            <label htmlFor="password">Password</label>
            <ReactPasswordStrength
              ref={ref => this.ReactPasswordStrength = ref}
              minLength={6}
              inputProps={{
                placeholder: '******',
                className: 'form-control-grey form-control--with-placeholder',
                id: 'password-guest',
                name: 'password',
              }}
              changeCallback={this.handlePasswordChange}
            />
            <div className={`error-message ${this.state.showErrors && this.state.validationErrors.password === 'Password is required' ? '' : 'is-hidden'}`}>
              <span className="text">Password is required</span>
            </div>
            <div className={`error-message ${this.state.showErrors && this.state.validationErrors.password === 'Is too weak' && this.state.password.length <= 5 ? '' : 'is-hidden'}`}>
              <span className="text">Please enter a longer password to continue</span>
            </div>
            <div className={`error-message ${this.state.showErrors && this.state.validationErrors.password === 'Is too weak' && this.state.password.length > 5 ? '' : 'is-hidden'}`}>
              <span className="text">Please enter a stronger password to continue</span>
            </div>
          </div>
          <button className="btn btn-primary btn-block" type="submit">Continue</button>
          <p className="terms-text">
            By continuing you confirm that you have read and accept our <a href="/pages/terms-and-conditions" target="_blank">Terms of Use</a>
          </p>
        </form>
      </div>
    )
  }
}

GuestRegister.propTypes = {
  email: PropTypes.string.isRequired,
  checkbox: PropTypes.node.isRequired,
}

export default GuestRegister
